/**
 * SchoolAccountSearch
 * Server-side account search for the Finance module. Loads matching accounts
 * from the full Chart of Accounts as the admin types, with paginated "load more"
 * so there is no hard cap on results. Value is the account code (string).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '../ui/input';
import { Search, X, Loader2 } from 'lucide-react';
import { schoolApi } from '../../api/school';

const PAGE = 50;

export const SchoolAccountSearch = ({ orgId, value, onChange, placeholder = 'Search code or name...', testid, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const boxRef = useRef(null);
  const debRef = useRef(null);

  const fetchPage = useCallback(async (q, sk, append) => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await schoolApi.listAccounts(orgId, { search: q, limit: PAGE, skip: sk });
      const arr = Array.isArray(res) ? res : (res.accounts || []);
      setResults((prev) => (append ? [...prev, ...arr] : arr));
      setHasMore(arr.length === PAGE);
    } catch { /* ignore transient errors */ }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => {
    if (!open) return undefined;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setSkip(0); fetchPage(query, 0, false); }, 250);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [query, open, fetchPage]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const loadMore = () => { const ns = skip + PAGE; setSkip(ns); fetchPage(query, ns, true); };
  const select = (acc) => { onChange(acc.code); setOpen(false); setQuery(''); };

  return (
    <div className="relative" ref={boxRef}>
      <div className={`flex items-center border rounded-md bg-background ${disabled ? 'opacity-60' : ''}`}>
        <Search className="w-4 h-4 ml-2 shrink-0 text-muted-foreground" />
        <Input
          value={open ? query : (value || '')}
          disabled={disabled}
          onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={value ? String(value) : placeholder}
          className="border-0 font-mono focus-visible:ring-0 focus-visible:ring-offset-0"
          data-testid={testid}
        />
        {value && !open && !disabled && (
          <button type="button" onClick={() => onChange('')} className="mr-2 shrink-0" aria-label="Clear account">
            <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 max-h-64 overflow-auto bg-popover border rounded-md shadow-lg" data-testid={testid ? `${testid}-dropdown` : undefined}>
          {loading && results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">{query ? 'No accounts found' : 'Type a code or name to search...'}</div>
          ) : (
            <>
              {results.map((a) => (
                <button
                  key={a.id || a.code}
                  type="button"
                  className="w-full text-left px-3 py-2 cursor-pointer hover:bg-accent flex items-center gap-2 text-sm"
                  onClick={() => select(a)}
                  data-testid={testid ? `${testid}-opt-${a.code}` : undefined}
                >
                  <span className="font-mono text-primary shrink-0">{a.code}</span>
                  <span className="truncate">{a.name || a.name_en || ''}</span>
                </button>
              ))}
              {hasMore && (
                <button
                  type="button"
                  onClick={loadMore}
                  className="w-full px-3 py-2 text-sm text-primary hover:bg-accent border-t"
                  data-testid={testid ? `${testid}-more` : undefined}
                >
                  {loading ? 'Loading...' : 'Load more results'}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SchoolAccountSearch;
